import {AxiosResponse, CancelToken} from 'axios';
import {Reminder} from '../backend/interfaces/reminder';
import {PostReminder} from '../backend/requests/post-reminder';
import {PutReminder} from '../backend/requests/put-reminder';
import {BackendApiManager} from '../managers/backend-api';

export class RemindersRepository {
  // Singleton instance
  private static _instance: RemindersRepository;

  static get instance(): RemindersRepository {
    if (!this._instance) {
      this._instance = new RemindersRepository();
    }
    return this._instance;
  }

  public async get(
    limit: number,
    skip: number,
    seniorId?: string,
    cancelToken?: CancelToken
  ): Promise<{totalCount: number; data: Array<Reminder>}> {
    const response: AxiosResponse<Array<Reminder>> = await BackendApiManager.instance.get<
      Array<Reminder>
    >(
      `/reminders?skip=${skip}&limit=${limit}${seniorId ? `&toUserId=${encodeURIComponent(seniorId)}` : ''}`,
      {
        cancelToken,
      }
    );

    const headerTotalCount = Number.parseInt(response.headers['x-total-count'] || '', 10);

    return {
      data: response.data,
      totalCount: Number.isNaN(headerTotalCount) ? response.data.length : headerTotalCount,
    };
  }

  public async create(body: PostReminder): Promise<Reminder> {
    const response: AxiosResponse<Reminder> = await BackendApiManager.instance.post<Reminder>(
      '/reminders',
      body
    );

    return response.data;
  }

  public async updateById(id: string, body: PutReminder): Promise<Reminder> {
    const response: AxiosResponse<Reminder> = await BackendApiManager.instance.put<Reminder>(
      `/reminders/${id}`,
      body
    );

    return response.data;
  }

  public async updateActiveStatusById(id: string, isActive: boolean): Promise<Reminder> {
    const response: AxiosResponse<{result: Reminder | null; isActive?: boolean}> =
      await BackendApiManager.instance.patch<{result: Reminder | null; isActive?: boolean}>(
        `/reminders/${id}/active/${isActive}`
      );

    if (!response.data?.result) {
      throw new Error('Reminder active status response did not include the updated reminder.');
    }

    return response.data.result;
  }

  public async deleteById(id: string): Promise<void> {
    await BackendApiManager.instance.delete<void>(`/reminders/${id}`);
  }

  public async getById(id: string): Promise<Reminder> {
    const response: AxiosResponse<Reminder> = await BackendApiManager.instance.get<Reminder>(
      `/reminders/${id}`
    );

    return response.data;
  }
}
